// =============================================================================
// @tnb/lot-status — Single source of truth for lot usability, consume priority,
// waste-type → lot_type mapping, and lifecycle transition SQL (WCO-3271).
// =============================================================================

'use strict';

/** Lifecycle statuses indicating on-hand usable stock (paired with lot_type). */
const USABLE_LIFECYCLE_STATUSES = Object.freeze(['available']);

/** Lot types usable for production when lifecycle status is available. */
const USABLE_TYPED_LOT_TYPES = Object.freeze(['remainder', 'by_product_p']);

/**
 * @deprecated WCO-3271 — use lot_type + lifecycle status. Kept for test migration only.
 * Previously mixed type-like values into status.
 */
const USABLE_LOT_STATUSES = Object.freeze(['available', 'remainder', 'by_product_p']);

/** ON CONFLICT target columns for ics.hte_lot accrual upserts. */
const LOT_BUSINESS_KEY_COLUMNS = Object.freeze([
  'item_number',
  'transfer_number',
  'lot_type',
  'is_ni_remnant',
]);

const LOT_BUSINESS_KEY_SQL = '(item_number, transfer_number, lot_type, is_ni_remnant)';

/**
 * ON CONFLICT target for accrual upserts (waste remainder / by-product / FP-NI).
 * The business key is enforced by TWO partial unique indexes so a rejected lot can coexist with
 * the available lot of the same (item, transfer, lot_type) — see WCO-3271. Postgres needs the
 * index predicate to infer a partial index, so accruals must carry `WHERE status <> 'rejected'`.
 * Rejected lots are written by materialIssue, never by these upserts.
 */
const LOT_ACCRUAL_CONFLICT_TARGET_SQL = `${LOT_BUSINESS_KEY_SQL} WHERE status <> 'rejected'`;

const SQL_FRAGMENT_RE = /^[a-zA-Z_][a-zA-Z0-9_.]*$/;

function assertSqlFragment(name, value) {
  if (typeof value !== 'string' || !value.trim()) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  if (!SQL_FRAGMENT_RE.test(value)) {
    throw new TypeError(`${name} must be a valid SQL column expression (got: ${value})`);
  }
}

/**
 * SQL predicate: lot is usable for production (available stock selection).
 * lot_type = classification; status = lifecycle (WCO-3271).
 *
 * @param {string} alias Table alias for ics.hte_lot (e.g. 'l', 'lot')
 * @returns {string} SQL boolean expression (parenthesized)
 */
function usableLotSql(alias) {
  assertSqlFragment('alias', alias);
  return `(
  (${alias}.lot_type IN ('remainder', 'by_product_p') AND ${alias}.status = 'available')
  OR (${alias}.lot_type = 'raw' AND ${alias}.status = 'available')
  OR (
    ${alias}.lot_type = 'raw'
    AND ${alias}.status = 'on_hold'
    AND ${alias}.approval_status = 'approved'
  )
)`;
}

/**
 * SQL filter for lots eligible for session deduction (usable + positive amount implied elsewhere).
 * @param {string} [alias='l']
 * @returns {string}
 */
function deductableLotSql(alias) {
  const a = alias || 'l';
  assertSqlFragment('alias', a);
  return `${a}.lot_type IN ('raw', 'remainder', 'by_product_p') AND ${a}.status = 'available'`;
}

/**
 * SQL CASE for FIFO consume ordering across usable lots.
 * Order: remainder → by_product_p → raw available → approved on_hold raw.
 *
 * @param {string} [alias='l'] Table alias for ics.hte_lot
 * @returns {string} SQL CASE expression
 */
function LOT_CONSUME_PRIORITY_SQL(alias) {
  const a = alias || 'l';
  assertSqlFragment('alias', a);
  return `CASE
  WHEN ${a}.lot_type = 'remainder' THEN 1
  WHEN ${a}.lot_type = 'by_product_p' THEN 2
  WHEN ${a}.lot_type = 'raw' AND ${a}.status = 'available' THEN 3
  WHEN ${a}.lot_type = 'raw' AND ${a}.status = 'on_hold' THEN 3
END`;
}

/**
 * Map waste_type to ics.hte_lot.lot_type for by-product categories.
 *
 * @param {string} col SQL column expression (e.g. 'wt.waste_type')
 * @returns {string} SQL CASE expression
 */
function WASTE_TYPE_TO_LOT_TYPE_SQL(col) {
  assertSqlFragment('col', col);
  return `CASE ${col}
  WHEN 'by_product_marketable' THEN 'by_product_m'
  WHEN 'by_product_production' THEN 'by_product_p'
  ELSE 'by_product_p' END`;
}

/** @deprecated WCO-3271 — use WASTE_TYPE_TO_LOT_TYPE_SQL (returns lot_type, not status). */
const WASTE_TYPE_TO_LOT_STATUS_SQL = WASTE_TYPE_TO_LOT_TYPE_SQL;

/**
 * JS mirror of {@link usableLotSql} for route-level validation.
 * @param {{ lot_type?: string | null, status?: string | null, approval_status?: string | null }} row
 * @returns {boolean}
 */
function isLotUsable(row) {
  const lotType = row.lot_type;
  const status = row.status;
  if (lotType === 'remainder' || lotType === 'by_product_p') {
    return status === 'available';
  }
  if (lotType === 'raw') {
    return status === 'available'
      || (status === 'on_hold' && row.approval_status === 'approved');
  }
  return false;
}

/**
 * JS mirror of {@link WASTE_TYPE_TO_LOT_TYPE_SQL} for by-product lot writes.
 * @param {string} wasteType
 * @returns {'by_product_p' | 'by_product_m'}
 */
function wasteTypeToLotType(wasteType) {
  if (wasteType === 'by_product_marketable') return 'by_product_m';
  if (wasteType === 'by_product_production') return 'by_product_p';
  return 'by_product_p';
}

/** @deprecated WCO-3271 — use wasteTypeToLotType. */
function wasteTypeToLotStatus(wasteType) {
  return wasteTypeToLotType(wasteType);
}

/**
 * SQL CASE: flip lifecycle to consumed when a lot depletes (WCO-3271).
 * Raw available → consumed; typed available → consumed; on_hold raw stays on_hold.
 *
 * @param {string} [alias='l']
 * @param {string} [newAmountExpr] Expression for remaining amount after deduct
 * @returns {string}
 */
function depleteLotLifecycleCase(alias, newAmountExpr) {
  const a = alias || 'l';
  assertSqlFragment('alias', a);
  const amt = newAmountExpr || `${a}.current_amount - $1`;
  return `CASE
    WHEN ${amt} <= 0 AND ${a}.lot_type = 'raw' AND ${a}.status = 'available' THEN 'consumed'
    WHEN ${amt} <= 0 AND ${a}.lot_type IN ('remainder', 'by_product_p', 'by_product_m', 'fp_ni') AND ${a}.status = 'available' THEN 'consumed'
    ELSE ${a}.status
  END`;
}

/**
 * SQL CASE: restore consumed lifecycle to available on weight restore.
 * @param {string} [alias='l']
 * @returns {string}
 */
function restoreLotLifecycleCase(alias) {
  const a = alias || 'l';
  assertSqlFragment('alias', a);
  return `CASE WHEN ${a}.status = 'consumed' THEN 'available' ELSE ${a}.status END`;
}

/**
 * SQL CASE for ON CONFLICT DO UPDATE — revive depleted row when accrual yields positive weight.
 * Exception: approved/on-hold raw stays on_hold (WCO-2411).
 *
 * @param {string} [tableRef='ics.hte_lot']
 * @param {string} [addedAmountExpr='EXCLUDED.current_amount']
 * @returns {string}
 */
function accrualReviveLifecycleCase(tableRef, addedAmountExpr) {
  const t = tableRef || 'ics.hte_lot';
  const add = addedAmountExpr || 'EXCLUDED.current_amount';
  return `CASE
    WHEN ${t}.current_amount + ${add} > 0
         AND NOT (${t}.lot_type = 'raw' AND ${t}.status = 'on_hold')
    THEN 'available'
    ELSE ${t}.status
  END`;
}

/** Stored statuses that encode material/lot type rather than lifecycle. */
const LOT_TYPE_STATUSES = Object.freeze({
  remainder: 'remainder',
  by_product_p: 'by_product_p',
  by_product_m: 'by_product_m',
  by_product: 'by_product_p',
});

/** Type-like stored status values (for SQL IN lists). */
const STORED_LOT_TYPE_STATUS_VALUES = Object.freeze([
  'remainder',
  'by_product_p',
  'by_product_m',
  'by_product',
]);

function isStoredLotTypeStatus(status) {
  return typeof status === 'string' && Object.prototype.hasOwnProperty.call(LOT_TYPE_STATUSES, status);
}

/**
 * @param {string | null | undefined} status
 * @returns {boolean}
 */
function isFinishProductNiStatus(status) {
  return typeof status === 'string' && status.startsWith('finish_product_ni');
}

/** Canonical lot_type values (WCO-3271). */
const LOT_TYPE_VALUES = Object.freeze(['raw', 'remainder', 'fp_ni', 'by_product_p', 'by_product_m']);

function isKnownLotType(lotType) {
  return typeof lotType === 'string' && LOT_TYPE_VALUES.includes(lotType);
}

/**
 * Derive presentation material type from a lot row (does not mutate stored columns).
 * WCO-3271: reads lot_type when present; falls back to legacy status inference pre-migration.
 *
 * @param {{ lot_type?: string | null, status?: string | null }} row
 * @returns {'raw' | 'remainder' | 'fp_ni' | 'by_product_p' | 'by_product_m'}
 */
function lotDisplayType(row) {
  const lotType = row?.lot_type;
  if (isKnownLotType(lotType)) {
    return lotType === 'by_product_m' ? 'by_product_m' : lotType;
  }
  const status = row?.status ?? '';
  if (isFinishProductNiStatus(status)) return 'fp_ni';
  if (isStoredLotTypeStatus(status)) return LOT_TYPE_STATUSES[status];
  return 'raw';
}

/**
 * Derive lifecycle status for UI display from a lot row (does not mutate stored columns).
 * WCO-3271: reads lifecycle status directly when lot_type is set; legacy fallback pre-migration.
 *
 * @param {{ lot_type?: string | null, status?: string | null, approval_status?: string | null, current_amount?: number | null }} row
 * @returns {string}
 */
function lotLifecycle(row) {
  const lotType = row?.lot_type;
  const status = row?.status ?? '';

  if (isKnownLotType(lotType)) {
    return status;
  }

  if (isFinishProductNiStatus(status)) {
    return hasOnHandLotWeight(row) ? 'available' : 'consumed';
  }

  if (isStoredLotTypeStatus(status)) {
    return hasOnHandLotWeight(row) ? 'available' : 'consumed';
  }

  return status;
}

/**
 * SQL predicate: exclude FP-NI lots (for Raw Material Lots list/count).
 * WCO-3271: uses lot_type; legacy status prefix fallback for unmigrated rows.
 *
 * @param {string} alias Table alias for ics.hte_lot
 * @returns {string} SQL boolean expression
 */
function RAW_MATERIAL_LOTS_SQL(alias) {
  assertSqlFragment('alias', alias);
  return `(
    COALESCE(${alias}.lot_type, '') <> 'fp_ni'
    AND COALESCE(${alias}.status, '') NOT LIKE 'finish_product_ni%'
  )`;
}

/**
 * SQL predicate matching {@link lotDisplayType} for the dashboard TYPE filter.
 *
 * WCO-3270: the Raw Material Lots TYPE column is labeled by FORM (Intact / Not Intact),
 * so the dropdown filters by form, not by the raw/remainder type value:
 *   - `intact`     = raw material that is intact = a raw lot OR an intact remainder
 *                    (NOT fp_ni, NOT by-product, is_ni_remnant = false)
 *   - `not_intact` = a not-intact remainder (loose pieces): lot_type='remainder' AND is_ni_remnant
 * The by-product values remain filterable directly. The legacy `raw`/`remainder` type values
 * are still accepted for any programmatic callers.
 *
 * @param {string} alias Table alias for ics.hte_lot
 * @param {string} displayType intact | not_intact | raw | remainder | by_product_p | by_product_m
 * @returns {string} SQL boolean expression (parenthesized)
 */
function LOT_DISPLAY_TYPE_FILTER_SQL(alias, displayType) {
  assertSqlFragment('alias', alias);
  const a = alias;
  const legacyTypeList = STORED_LOT_TYPE_STATUS_VALUES.map((s) => `'${s}'`).join(', ');
  switch (displayType) {
    case 'intact':
      return `(
        ${RAW_MATERIAL_LOTS_SQL(a)}
        AND COALESCE(${a}.lot_type, 'raw') NOT IN ('by_product_p', 'by_product_m')
        AND COALESCE(${a}.is_ni_remnant, false) = false
      )`;
    case 'not_intact':
      return `(
        (${a}.lot_type = 'remainder' OR (${a}.lot_type IS NULL AND ${a}.status = 'remainder'))
        AND COALESCE(${a}.is_ni_remnant, false) = true
      )`;
    case 'raw':
      return `(
        ${RAW_MATERIAL_LOTS_SQL(a)}
        AND (
          COALESCE(${a}.lot_type, 'raw') = 'raw'
          OR (${a}.lot_type IS NULL AND ${a}.status NOT IN (${legacyTypeList}))
        )
      )`;
    case 'remainder':
      return `(${a}.lot_type = 'remainder' OR (${a}.lot_type IS NULL AND ${a}.status = 'remainder'))`;
    case 'by_product_p':
      return `(
        ${a}.lot_type = 'by_product_p'
        OR (${a}.lot_type IS NULL AND ${a}.status IN ('by_product_p', 'by_product'))
      )`;
    case 'by_product_m':
      return `(
        ${a}.lot_type = 'by_product_m'
        OR (${a}.lot_type IS NULL AND ${a}.status = 'by_product_m')
      )`;
    default:
      throw new TypeError(`Unsupported lot display type filter: ${displayType}`);
  }
}

/**
 * SQL predicate matching {@link lotLifecycle} for dashboard lifecycle filter.
 * WCO-3271: filters on lifecycle status column; legacy status-as-type fallback pre-migration.
 *
 * @param {string} alias Table alias for ics.hte_lot
 * @param {string} lifecycle available | consumed | on_hold_approved | on_hold_pending | rejected | remaining
 * @returns {string} SQL boolean expression (parenthesized where needed)
 */
function LOT_LIFECYCLE_FILTER_SQL(alias, lifecycle) {
  assertSqlFragment('alias', alias);
  const a = alias;
  const legacyTypeList = STORED_LOT_TYPE_STATUS_VALUES.map((s) => `'${s}'`).join(', ');
  switch (lifecycle) {
    case 'available':
      return `(
        ${a}.status = 'available'
        OR (
          ${a}.lot_type IS NULL
          AND ${a}.status IN (${legacyTypeList})
          AND COALESCE(${a}.current_amount, 0) > 0
        )
      )`;
    case 'consumed':
      return `(
        ${a}.status = 'consumed'
        OR (
          ${a}.lot_type IS NULL
          AND ${a}.status IN (${legacyTypeList})
          AND COALESCE(${a}.current_amount, 0) <= 0
        )
      )`;
    case 'on_hold_approved':
      return `${a}.status = 'on_hold' AND ${a}.approval_status = 'approved' AND ${a}.current_amount > 0`;
    case 'on_hold_pending':
      return `${a}.status = 'on_hold' AND (${a}.approval_status = 'pending' OR ${a}.approval_status IS NULL)`;
    case 'rejected':
      return `${a}.status = 'rejected'`;
    case 'remaining':
      return `${a}.status != 'consumed' AND ${a}.current_amount > 0
        AND (${a}.status != 'on_hold' OR ${a}.approval_status = 'approved')`;
    default:
      throw new TypeError(`Unsupported lot lifecycle filter: ${lifecycle}`);
  }
}

/**
 * Whether the lot row has on-hand weight (current_amount > 0).
 *
 * @param {{ current_amount?: number | null }} row
 * @returns {boolean}
 */
function hasOnHandLotWeight(row) {
  return Number(row?.current_amount ?? 0) > 0;
}

/**
 * Whether a lot belongs in the Raw Material Lots dashboard section (excludes FP-NI).
 *
 * @param {{ lot_type?: string | null, status?: string | null }} row
 * @returns {boolean}
 */
function isRawMaterialLotRow(row) {
  return lotDisplayType(row) !== 'fp_ni';
}

module.exports = {
  USABLE_LIFECYCLE_STATUSES,
  USABLE_TYPED_LOT_TYPES,
  USABLE_LOT_STATUSES,
  LOT_BUSINESS_KEY_COLUMNS,
  LOT_BUSINESS_KEY_SQL,
  LOT_ACCRUAL_CONFLICT_TARGET_SQL,
  LOT_TYPE_STATUSES,
  STORED_LOT_TYPE_STATUS_VALUES,
  usableLotSql,
  deductableLotSql,
  LOT_CONSUME_PRIORITY_SQL,
  WASTE_TYPE_TO_LOT_TYPE_SQL,
  WASTE_TYPE_TO_LOT_STATUS_SQL,
  isLotUsable,
  wasteTypeToLotType,
  wasteTypeToLotStatus,
  depleteLotLifecycleCase,
  restoreLotLifecycleCase,
  accrualReviveLifecycleCase,
  isFinishProductNiStatus,
  isStoredLotTypeStatus,
  lotDisplayType,
  lotLifecycle,
  hasOnHandLotWeight,
  isRawMaterialLotRow,
  RAW_MATERIAL_LOTS_SQL,
  LOT_DISPLAY_TYPE_FILTER_SQL,
  LOT_LIFECYCLE_FILTER_SQL,
};
